import { SignUpFormInput } from '@jetshop/core/components/Auth/formatSignupInput';
import { formatSignupInput } from './formatSignupInput';
import useAuth from '@jetshop/core/components/AuthContext/useAuth';
import SignupMutation from '@jetshop/core/data/mutations/signup.gql';
import getErrorDetail, {
  ErrorDetail,
} from '@jetshop/core/helpers/getErrorDetail';
import { Mutation, MutationSignUpArgs } from '@jetshop/core/types';
import t from '@jetshop/intl';
import { FormikActions } from 'formik';
import { useApolloClient, useMutation } from '@apollo/react-hooks';

export function useSignupSubmit() {
  const [mutate] = useMutation<
    { signUp: Mutation['signUp'] },
    MutationSignUpArgs
  >(SignupMutation);
  const client = useApolloClient();
  const { logIn } = useAuth();

  return function signup({
    values,
    actions,
  }: {
    values: SignUpFormInput;
    actions: FormikActions<SignUpFormInput>;
  }) {
    actions.setSubmitting(true);
    mutate({
      variables: {
        input: formatSignupInput(values),
      },
    })
      .then(
        (resp) => {
          // Submitting is done
          actions.setSubmitting(false);
          // Reset the Apollo store
          client.resetStore().then(() => {
            // Log the user in with the token from signup
            logIn(resp.data.signUp.token.value);
          });
        },
        (error) => {
          console.log(getErrorDetail(error));
          const errorDetail = getErrorDetail(error);
          // Submitting is done
          actions.setSubmitting(false);

          if (errorDetail.codes.includes('InvalidCreateCustomerInput')) {
            // Errors UI reads validationDetails + isSignupError
            actions.setStatus({
              validationDetails: getValidationDetails(errorDetail),
              isSignupError: true,
              globalError: getErrorMsg(errorDetail),
            });
          } else {
            // Generic error
            actions.setStatus({
              globalError: t('Something went wrong. Please try again.'),
            });
          }
        }
      )
      .catch((err) => {
        console.log(err);
        actions.setSubmitting(false);
      });
  };
}

/** extensions.data → ['Pid', 'UniqueValueAlreadyExists'] for the Errors UI */
function getValidationDetails(errorDetail: ErrorDetail) {
  const raw = errorDetail.data as
    | Array<Record<string, string[]>>
    | Record<string, string[]>
    | undefined;
  const first = Array.isArray(raw) ? raw[0] : raw;
  if (!first || typeof first !== 'object') return ['ERROR', 'InvalidValue'];

  const [field] = Object.keys(first);
  const [detail] = first[field] ?? [];
  return [field, detail ?? 'InvalidValue'];
}

function getErrorMsg(errorDetail: ErrorDetail) {
  const data = errorDetail.data as
    | Array<Record<string, string[]>>
    | undefined;

  const emailError = data?.find?.((c) => Object.keys(c)[0] === 'EmailAddress');
  const pidError = data?.find?.((c) => Object.keys(c)[0] === 'Pid');

  if (
    emailError &&
    Object.values(emailError)[0][0] === 'UniqueValueAlreadyExists'
  ) {
    return t('You have already signed up with this email address');
  }

  if (
    pidError &&
    Object.values(pidError)[0][0] === 'UniqueValueAlreadyExists'
  ) {
    return t(
      'This social security number is already used in another account. Please contact customer service.'
    );
  }

  return t('Something went wrong. Please try again.');
}
